/**
 * Auth Client - Extracted from working AuthContext.js
 * Removed React dependencies, kept all working logic
 */

import { 
  CognitoIdentityProviderClient, 
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  UpdateUserAttributesCommand
} from '@aws-sdk/client-cognito-identity-provider';
import { REGION, USER_POOL_CLIENT_ID as CLIENT_ID } from './aws-cognito-config';

// Token storage keys - COPIED EXACTLY from working AuthContext.js
const ACCESS_TOKEN_KEY = 'uncola_access_token';
const ID_TOKEN_KEY = 'uncola_id_token';
const REFRESH_TOKEN_KEY = 'uncola_refresh_token';
const TOKEN_EXPIRY_KEY = 'uncola_token_expiry';
const USER_KEY = 'uncola_user';

// Parse JWT token - COPIED EXACTLY from working AuthContext.js
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing JWT:', e);
    return {};
  }
};

// Check if token is expired - COPIED EXACTLY from working AuthContext.js
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const decoded = parseJwt(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (e) {
    return true;
  }
};

export const createAuthClient = () => {
  // Implement singleton pattern for cross-MFE compatibility
  const GLOBAL_KEY = '__UNCOLA_AUTH_CLIENT__';
  
  if (typeof window !== 'undefined' && window[GLOBAL_KEY]) {

    return window[GLOBAL_KEY];
  }
  
  const instanceId = Math.random().toString(36).substr(2, 9);
  const createdAt = new Date().toISOString();

  
  const cognitoClient = new CognitoIdentityProviderClient({ region: REGION });
  
  // Sign in function - COPIED EXACTLY from working AuthContext.js
  // Only removed: useCallback wrapper, setState calls
  const signIn = async (username, password) => {
    console.log('🔐 AuthClient: signIn function called with username:', username);
    
    try {
      console.log('🔐 AuthClient: Creating InitiateAuthCommand...');
      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password
        }
      });
      
      console.log('🔐 AuthClient: Sending command to Cognito...');
      const response = await cognitoClient.send(command);
      const { AuthenticationResult } = response;
      
      if (!AuthenticationResult) {
        console.error('🔐 AuthClient: No AuthenticationResult in response');
        throw new Error('Authentication failed');
      }
      
      console.log('🔐 AuthClient: AuthenticationResult received:', {
        hasAccessToken: !!AuthenticationResult.AccessToken,
        hasIdToken: !!AuthenticationResult.IdToken,
        hasRefreshToken: !!AuthenticationResult.RefreshToken,
        expiresIn: AuthenticationResult.ExpiresIn
      });
      
      const { AccessToken, IdToken, RefreshToken, ExpiresIn } = AuthenticationResult;
      
      // Calculate expiry time
      const expiryTime = Date.now() + ExpiresIn * 1000;
      
      console.log('🔐 AuthClient: About to store tokens in localStorage');
      
      // Store tokens in localStorage - exactly as in AuthContext.js
      localStorage.setItem(ACCESS_TOKEN_KEY, AccessToken);
      console.log('💾 AuthClient: Stored ACCESS_TOKEN');
      localStorage.setItem(ID_TOKEN_KEY, IdToken);
      console.log('💾 AuthClient: Stored ID_TOKEN');
      localStorage.setItem(REFRESH_TOKEN_KEY, RefreshToken);
      console.log('💾 AuthClient: Stored REFRESH_TOKEN');
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      console.log('💾 AuthClient: Stored TOKEN_EXPIRY');
      
      // Parse user info from ID token
      const userInfo = parseJwt(IdToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
      
      console.log('🔐 AuthClient: Tokens stored in localStorage. Verifying storage...');
      console.log('🔐 AuthClient: localStorage verification:', {
        storedAccessToken: !!localStorage.getItem(ACCESS_TOKEN_KEY),
        storedIdToken: !!localStorage.getItem(ID_TOKEN_KEY),
        storedRefreshToken: !!localStorage.getItem(REFRESH_TOKEN_KEY),
        storedExpiry: localStorage.getItem(TOKEN_EXPIRY_KEY),
        accessTokenLength: localStorage.getItem(ACCESS_TOKEN_KEY)?.length,
        idTokenLength: localStorage.getItem(ID_TOKEN_KEY)?.length
      });
      
      console.log('🔐 AuthClient: User info stored:', userInfo);
      console.log('Token Expiry:', new Date(expiryTime).toISOString());
      console.log('User Info:', userInfo);
      
      // Dispatch custom event for cross-micro-frontend sync
      console.log('🔐 AuthClient: Dispatching cross-MFE auth change event');
      window.dispatchEvent(new CustomEvent('auth:change', {
        detail: { isAuthenticated: true, user: userInfo }
      }));
      
      // Notify subscribers of the auth state change
      notifySubscribers();
      
      console.log('🔐 AuthClient: signIn completed successfully, returning userInfo');
      return userInfo;
    } catch (error) {
      console.error('🔐 AuthClient: Sign in error:', error);
      throw error;
    }
  };
  
  // Track refresh in progress to prevent concurrent refresh calls
  let refreshPromise = null;
  
  // COPIED EXACTLY from working AuthContext.js refreshTokens function
  // Only removed: useCallback wrapper, setAccessToken, setIdToken, setUser, setIsAuthenticated calls
  const refreshTokens = async () => {
    // If refresh is already in progress, return the existing promise
    if (refreshPromise) {
      console.log('🔄 AuthClient: Refresh already in progress, waiting for existing refresh...');
      return refreshPromise;
    }
    
    console.log('🔄 AuthClient: refreshTokens called', {
      instanceId,
      timestamp: new Date().toISOString()
    });
    
    // Create and store the refresh promise
    refreshPromise = (async () => {
      try {
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      console.log('🔄 AuthClient: Checking refresh token:', {
        hasRefreshToken: !!storedRefreshToken,
        refreshTokenLength: storedRefreshToken?.length,
        timestamp: new Date().toISOString()
      });
      
      if (!storedRefreshToken) {
        throw new Error('No refresh token available');
      }
      
      console.log('🔄 AuthClient: Initiating token refresh with Cognito...', {
        clientId: CLIENT_ID,
        timestamp: new Date().toISOString()
      });
      
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          REFRESH_TOKEN: storedRefreshToken
        }
      });
      
      console.log('🔄 AuthClient: Sending refresh request to Cognito...');
      const response = await cognitoClient.send(command);
      console.log('🔄 AuthClient: Cognito refresh response received');
      
      const { AuthenticationResult } = response;
      
      console.log('🔄 AuthClient: Cognito refresh response:', {
        hasAuthResult: !!AuthenticationResult,
        hasAccessToken: !!AuthenticationResult?.AccessToken,
        hasIdToken: !!AuthenticationResult?.IdToken,
        expiresIn: AuthenticationResult?.ExpiresIn
      });
      
      if (!AuthenticationResult) {
        throw new Error('Token refresh failed - no AuthenticationResult');
      }
      
      const { AccessToken, IdToken, ExpiresIn } = AuthenticationResult;
      
      // Calculate expiry time
      const expiryTime = Date.now() + ExpiresIn * 1000;
      
      console.log('🔄 AuthClient: Storing refreshed tokens...', {
        timestamp: new Date().toISOString()
      });
      
      // Store tokens in localStorage
      localStorage.setItem(ACCESS_TOKEN_KEY, AccessToken);
      console.log('💾 AuthClient: Stored refreshed ACCESS_TOKEN');
      localStorage.setItem(ID_TOKEN_KEY, IdToken);
      console.log('💾 AuthClient: Stored refreshed ID_TOKEN');
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      console.log('💾 AuthClient: Stored refreshed TOKEN_EXPIRY');
      
      // Parse user info from ID token
      const userInfo = parseJwt(IdToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
      console.log('💾 AuthClient: Stored refreshed USER_KEY');
      
      console.log('✅ AuthClient: Token refresh completed successfully:', {
        userEmail: userInfo.email,
        newExpiry: new Date(expiryTime).toISOString()
      });
      
      // Dispatch cross-MFE auth change event
      console.log('🔄 AuthClient: Dispatching cross-MFE auth change event after refresh');
      window.dispatchEvent(new CustomEvent('auth:change', {
        detail: { isAuthenticated: true, user: userInfo }
      }));
      
      // Notify subscribers of the auth state change
      notifySubscribers();
      
      return userInfo;
      } catch (error) {
        console.error('❌ AuthClient: Token refresh error:', {
          error: error.message,
          errorName: error.name,
          errorCode: error.$metadata?.httpStatusCode,
          timestamp: new Date().toISOString(),
          fullError: error
        });
        console.log('🚪 AuthClient: Refresh failed, clearing tokens and signing out...');
        signOut();
        throw error;
      } finally {
        // Clear the refresh promise so future calls can proceed
        refreshPromise = null;
      }
    })();
    
    return refreshPromise;
  };

  // COPIED EXACTLY from working AuthContext.js signOut function
  // Only removed: useCallback wrapper, setState calls, navigate call
  const signOut = () => {
    console.log('🚪 AuthClient: Sign out called - CLEARING ALL TOKENS', {
      instanceId,
      stackTrace: new Error().stack,
      beforeClear: {
        hasAccessToken: !!localStorage.getItem(ACCESS_TOKEN_KEY),
        hasIdToken: !!localStorage.getItem(ID_TOKEN_KEY),
        hasRefreshToken: !!localStorage.getItem(REFRESH_TOKEN_KEY)
      }
    });
    
    // Clear tokens from localStorage
    console.log('🚪 AuthClient: About to clear tokens...');
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    console.log('🚪 AuthClient: Cleared ACCESS_TOKEN_KEY');
    localStorage.removeItem(ID_TOKEN_KEY);
    console.log('🚪 AuthClient: Cleared ID_TOKEN_KEY');
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    console.log('🚪 AuthClient: Cleared REFRESH_TOKEN_KEY');
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    console.log('🚪 AuthClient: Cleared TOKEN_EXPIRY_KEY');
    localStorage.removeItem(USER_KEY);
    console.log('🚪 AuthClient: Cleared USER_KEY');
    
    console.log('🚪 AuthClient: Sign out complete', {
      instanceId,
      afterClear: {
        hasAccessToken: !!localStorage.getItem(ACCESS_TOKEN_KEY),
        hasIdToken: !!localStorage.getItem(ID_TOKEN_KEY),
        hasRefreshToken: !!localStorage.getItem(REFRESH_TOKEN_KEY),
        allKeys: Object.keys(localStorage).filter(key => key.startsWith('uncola_'))
      }
    });
    
    console.log('🔐 AuthClient: All tokens cleared from localStorage', {
      instanceId,
      afterClear: {
        hasAccessToken: !!localStorage.getItem(ACCESS_TOKEN_KEY),
        hasIdToken: !!localStorage.getItem(ID_TOKEN_KEY),
        hasRefreshToken: !!localStorage.getItem(REFRESH_TOKEN_KEY)
      }
    });
    
    // Dispatch cross-MFE auth change event
    console.log('🔄 AuthClient: Dispatching cross-MFE auth change event after sign out');
    window.dispatchEvent(new CustomEvent('auth:change', {
      detail: { isAuthenticated: false, user: null }
    }));
    
    // Notify subscribers of the auth state change
    notifySubscribers();
    
    console.log('🔐 AuthClient: Sign out completed', { instanceId });
  };

  const getAuthState = () => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const idToken = localStorage.getItem(ID_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    
    const hasTokens = !!(accessToken && idToken);
    
    // If no tokens, return unauthenticated state
    if (!hasTokens) {
      return { isAuthenticated: false, accessToken: null, idToken: null, user: null };
    }
    
    // Check if token is expired
    const tokenExpired = isTokenExpired(idToken);
    
    // If token is expired but we have a refresh token, trigger refresh in background
    // Return authenticated so user isn't kicked to login — getValidToken() will
    // refresh synchronously before any API call, and redirect to login if refresh fails
    if (tokenExpired && refreshToken) {
      console.log('🔄 AuthClient: Token expired, triggering refresh in background...');
      setTimeout(() => {
        refreshTokens().catch(err => {
          console.error('Background token refresh failed:', err);
        });
      }, 0);
      
      let user = null;
      if (userStr) {
        try { user = JSON.parse(userStr); } catch (e) {}
      }
      return { isAuthenticated: true, accessToken, idToken, user };
    }
    
    // If token is expired and no refresh token, return unauthenticated
    if (tokenExpired) {
      console.log('🔄 AuthClient: Token expired and no refresh token available');
      return { isAuthenticated: false, accessToken: null, idToken: null, user: null };
    }
    
    // Token is valid
    let user = null;
    if (userStr) {
      try {
        user = JSON.parse(userStr);
      } catch (e) {
        console.error('Error parsing user info:', e);
      }
    }
    
    return { isAuthenticated: true, accessToken, idToken, user };
  };

  // Registration function - converted from existing registrationService.js
  const registerUser = async (userData) => {
    try {
      const command = new SignUpCommand({
        ClientId: CLIENT_ID,
        Username: userData.email,
        Password: userData.password,
        UserAttributes: [
          { Name: 'email', Value: userData.email },
          { Name: 'given_name', Value: userData.firstName },
          { Name: 'family_name', Value: userData.lastName }
        ]
      });

      const response = await cognitoClient.send(command);
      return {
        user: response.UserSub,
        userConfirmed: response.UserConfirmed,
        userSub: response.UserSub
      };
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  // Confirm registration function - converted from existing registrationService.js
  const confirmRegistration = async (username, code) => {
    try {
      const command = new ConfirmSignUpCommand({
        ClientId: CLIENT_ID,
        Username: username,
        ConfirmationCode: code
      });

      const response = await cognitoClient.send(command);
      return response;
    } catch (error) {
      console.error('Confirmation error:', error);
      throw error;
    }
  };

  // Resend confirmation code function - converted from existing registrationService.js
  const resendConfirmationCode = async (username) => {
    try {
      const command = new ResendConfirmationCodeCommand({
        ClientId: CLIENT_ID,
        Username: username
      });

      const response = await cognitoClient.send(command);
      return response;
    } catch (error) {
      console.error('Resend code error:', error);
      throw error;
    }
  };

  // Get a valid access token - handles refresh automatically
  const getValidToken = async () => {
    console.log('🔐 AuthClient: getValidToken called [ID_TOKEN_VERSION] - Instance:', instanceId);
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const idToken = localStorage.getItem(ID_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    
    console.log('🔐 AuthClient: getValidToken called - Token check [ID_TOKEN_VERSION]:', {
      hasAccessToken: !!accessToken,
      hasIdToken: !!idToken,
      hasRefreshToken: !!refreshToken,
      accessTokenLength: accessToken?.length,
      idTokenLength: idToken?.length,
      instanceId: instanceId
    });
    
    // If no tokens at all, redirect to login
    if (!accessToken || !idToken) {
      console.log('🔐 AuthClient: No tokens found, redirecting to login');
      signOut();
      window.location.href = '/login';
      return null;
    }
    
    // If ID token is not expired, return it
    if (!isTokenExpired(idToken)) {
      console.log('🔐 AuthClient: Returning ID token (not expired) [ID_TOKEN_VERSION]');
      return idToken;
    }
    
    // Token is expired, try to refresh
    console.log('⏰ AuthClient: ID token EXPIRED, attempting refresh...', {
      hasRefreshToken: !!refreshToken,
      tokenExpiry: parseJwt(idToken)?.exp,
      currentTime: Date.now() / 1000
    });
    
    if (refreshToken) {
      console.log('🔄 AuthClient: Starting token refresh with refresh token...');
      try {
        await refreshTokens();
        const newIdToken = localStorage.getItem(ID_TOKEN_KEY);
        console.log('✅ AuthClient: Token refresh successful, returning new ID token');
        return newIdToken;
      } catch (error) {
        console.error('❌ AuthClient: Token refresh failed, redirecting to login:', error);
        signOut();
        window.location.href = '/login';
        return null;
      }
    } else {
      console.log('❌ AuthClient: No refresh token available, redirecting to login');
      signOut();
      window.location.href = '/login';
      return null;
    }
  };
  
  // Update user attributes function
  const updateUserAttributes = async (attributes) => {
    try {
      // UpdateUserAttributes requires the Cognito access token (not the ID token).
      // getValidToken() returns the ID token, so read the access token directly from storage.
      // If access token is expired, refresh first (refreshTokens updates both tokens).
      let accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (accessToken && isTokenExpired(accessToken)) {
        await refreshTokens();
        accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      }
      if (!accessToken) {
        throw new Error('No valid access token available');
      }

      const userAttributes = Object.entries(attributes).map(([name, value]) => ({
        Name: name,
        Value: value
      }));

      const command = new UpdateUserAttributesCommand({
        AccessToken: accessToken,
        UserAttributes: userAttributes
      });

      const response = await cognitoClient.send(command);
      
      // Update local user data
      const userStr = localStorage.getItem(USER_KEY);
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const updatedUser = { ...user, ...attributes };
          localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
          
          // Notify subscribers of the auth state change
          notifySubscribers();
        } catch (e) {
          console.error('Error updating local user data:', e);
        }
      }
      
      return response;
    } catch (error) {
      console.error('Update user attributes error:', error);
      throw error;
    }
  };
  // Check if refresh token exists
  const hasRefreshToken = () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    return !!refreshToken && refreshToken.length > 0;
  };
  
  // Subscribe to auth state changes
  let subscribers = [];
  const subscribe = (callback) => {
    subscribers.push(callback);
    return () => {
      subscribers = subscribers.filter(cb => cb !== callback);
    };
  };
  
  // Notify subscribers of auth state changes
  const notifySubscribers = () => {
    const currentState = getAuthState();
    subscribers.forEach(callback => callback(currentState));
  };

  const getCurrentUserId = () => {
    const authState = getAuthState();
    console.log('🔍 getCurrentUserId - authState:', { 
      hasUser: !!authState?.user, 
      userSub: authState?.user?.sub,
      hasIdToken: !!authState?.idToken 
    });
    
    // Try to get from user object first
    if (authState?.user?.sub) {
      console.log('🔍 getCurrentUserId - returning from user.sub:', authState.user.sub);
      return authState.user.sub;
    }
    
    // Fallback: decode idToken
    if (authState?.idToken) {
      try {
        const payload = JSON.parse(atob(authState.idToken.split('.')[1]));
        console.log('🔍 getCurrentUserId - decoded token payload.sub:', payload.sub);
        return payload.sub;
      } catch (e) {
        console.error('Error decoding idToken:', e);
      }
    }
    
    console.log('🔍 getCurrentUserId - returning null');
    return null;
  };

  const isAdmin = () => {
    const authState = getAuthState();
    if (!authState?.idToken) return false;
    try {
      const payload = JSON.parse(atob(authState.idToken.split('.')[1]));
      // Server sets this claim (pre-token trigger). UI just reads the boolean.
      return payload.isAdmin === 'true' || payload.isAdmin === true;
    } catch (e) {
      return false;
    }
  };

  const authClientInstance = { 
    signIn, 
    registerUser, 
    confirmRegistration, 
    resendConfirmationCode, 
    refreshTokens, 
    signOut, 
    getAuthState, 
    getValidToken,
    getCurrentUserId,
    isAdmin,
    hasRefreshToken,
    updateUserAttributes,
    subscribe,
    _instanceId: instanceId,
    _createdAt: createdAt
  };
  
  // Store singleton instance in global window for cross-MFE access
  if (typeof window !== 'undefined') {
    window[GLOBAL_KEY] = authClientInstance;

  }
  
  return authClientInstance;
};
